/**
 * All user-facing strings for the new pricing section, EN + DE, following the
 * hard-coded-dictionary pattern used by pricingSectionV2/quiz/quizCopy.ts
 * (the repo has no i18n library). `br` falls back to EN.
 *
 * Strings and tooltip copy come verbatim from IQ-Capital-Pricing-Spec-for-IT.md
 * (§7 tooltips, German translation reference). Numbers, product names,
 * platform names and trading terms stay in English per the spec's
 * DO-NOT-TRANSLATE list.
 */

import type { ProductId } from "@/lib/new-pricing/products";

export type KpiKey =
  | "profitTarget"
  | "profitSplit"
  | "payoutEvery"
  | "maxDrawdown"
  | "drawdownMode"
  | "maxPositionLoss"
  | "dailyLossLimit"
  | "consistency"
  | "duration"
  | "activationFee"
  | "resetFee"
  | "extensionFee"
  | "maxAccounts";

export interface NewPricingCopy {
  heroEyebrow: string;
  heroTitleLine1: string;
  heroTitleAccent: string;
  heroSub: string;

  markets: { futures: string; cfdCrypto: string };
  integratedWith: { cfdCrypto: string; futures: string };
  badgeNew: string;
  badgeMostPopular: string;

  products: Record<ProductId, { name: string; tag: string; info: string }>;

  stage: { challenge: string; funded: string; instantOnly: string };
  keyFeatures: string;
  chosenPath: string;
  variantLabels: {
    mpl: string;
    dll: string;
    classic: string;
    daily: string;
  };

  kpiLabels: Record<KpiKey, string>;
  kpiTooltips: Record<KpiKey, string>;

  values: {
    none: string;
    free: string;
    dash: string;
    /** the Drawdown Mode cell value, e.g. "EOD Trailing" */
    drawdownMode: string;
    days: (n: number) => string; // duration "30 Days"
    payoutDays: (n: number) => string; // "10 days" / "1 day"
  };

  price: { label: string; account: string; oneTimeFee: string };
  sizeUnavailableReason: string;

  footnotes: { mpl: string; dll: string };

  cta: {
    getPlan: string;
    startChallenge: string;
    getFunded: string;
    /** mobile split cards: jump to the sibling variant card, e.g. "View Classic" */
    viewVariant: (label: string) => string;
  };

  unsure: { title: string; sub: string; helpMeChoose: string; compare: string };

  compare: {
    title: string;
    sub: string;
    legendMpl: string;
    legendDll: string;
    oneTimeFee: string;
    challenge: string;
    emptySlot: string;
    mobileHint: string;
  };

  platformsSection: {
    eyebrow: string;
    titleLine1: string;
    titleAccent: string;
    sub: string;
  };
}

const EN: NewPricingCopy = {
  heroEyebrow: "Build your plan",
  heroTitleLine1: "Fund Your Edge &",
  heroTitleAccent: "Scale Your Trading.",
  heroSub: "Pick your product, pass once, get paid. Choose the account that matches how you trade.",

  markets: { futures: "Futures", cfdCrypto: "CFD + Crypto" },
  integratedWith: {
    cfdCrypto: "Crypto & CFD integrated with",
    futures: "Futures integrated with",
  },
  badgeNew: "New",
  badgeMostPopular: "Most Popular",

  products: {
    core: {
      name: "Core",
      tag: "One-Day-Pass",
      info: "Our classic evaluation and most affordable route. Reach the profit target without breaching your drawdown — you can pass in as little as one day — then trade a funded account.",
    },
    rapid: {
      name: "Rapid",
      tag: "Faster Payouts",
      info: "Built for speed. Lower profit targets and, once funded, payouts as often as every day. Free activation and free retries.",
    },
    instant: {
      name: "Instant Funding",
      tag: "Skip the challenge",
      info: "No evaluation at all. Pay once and trade a funded account from day one, keeping 90% of your profits.",
    },
  },

  stage: { challenge: "Challenge", funded: "After you pass", instantOnly: "Funded from day one" },
  keyFeatures: "Key features",
  chosenPath: "Chosen Path",
  variantLabels: {
    mpl: "Max Position Loss",
    dll: "Daily Loss Limit",
    classic: "Classic",
    daily: "Daily",
  },

  kpiLabels: {
    profitTarget: "Profit Target",
    profitSplit: "Profit Split",
    payoutEvery: "Payout Every",
    maxDrawdown: "Max Drawdown",
    drawdownMode: "Drawdown Mode",
    maxPositionLoss: "Max Position Loss",
    dailyLossLimit: "Daily Loss Limit",
    consistency: "Consistency",
    duration: "Duration",
    activationFee: "Activation Fee",
    resetFee: "Reset Fee",
    extensionFee: "Extension Fee",
    maxAccounts: "Max Accounts",
  },

  kpiTooltips: {
    profitTarget: "The profit you must reach to pass the evaluation.",
    profitSplit: "Your share of the profits. 90 / 10 = you keep 90%.",
    payoutEvery: "How often you can request a withdrawal.",
    maxDrawdown: "The maximum your balance may fall from the starting balance before the account is breached.",
    drawdownMode:
      "EOD Trailing: the max-loss line trails your highest end-of-day balance — it moves up as you profit and never down.",
    maxPositionLoss: "Max loss allowed on a single open position. 2 soft breaches forgiven, the 3rd is a hard breach.",
    dailyLossLimit: "Max you may lose within one trading day. 1 soft breach forgiven, the 2nd is a hard breach.",
    consistency: "No single day may exceed this share of your total profit.",
    duration: "Time limit to complete the challenge.",
    activationFee: "One-time fee to activate the funded account after you pass.",
    resetFee: "Cost to restart the account after a breach.",
    extensionFee: "Fee to extend the duration of the challenge.",
    maxAccounts: "How many funded accounts you can run in parallel.",
  },

  values: {
    none: "None",
    free: "Free",
    dash: "—",
    drawdownMode: "EOD Trailing",
    days: (n) => `${n} Days`,
    payoutDays: (n) => (n === 1 ? "1 day" : `${n} days`),
  },

  price: { label: "Price", account: "Account", oneTimeFee: "one-time fee" },
  sizeUnavailableReason: "Not available on this platform",

  footnotes: {
    mpl: "* Max Position Loss: 2 soft breaches forgiven, the 3rd is a hard breach.",
    dll: "* Daily Loss Limit: 1 soft breach forgiven, the 2nd is a hard breach.",
  },

  cta: {
    getPlan: "Get Plan",
    startChallenge: "Start Challenge",
    getFunded: "Get Funded",
    viewVariant: (label) => `View ${label}`,
  },

  unsure: {
    title: "Still unsure which product suits you?",
    sub: "Add plans side by side and instantly see what sets them apart.",
    helpMeChoose: "Help me choose",
    compare: "Compare",
  },

  compare: {
    title: "Compare plans",
    sub: "Tick up to 4 plans. Highlighted rows are where they differ.",
    legendMpl: "*MPL = Max Position Loss",
    legendDll: "*DLL = Daily Loss Limit",
    oneTimeFee: "One-Time Fee",
    challenge: "Challenge",
    emptySlot: "Pick a plan above",
    mobileHint: "Swipe to see more plans",
  },

  platformsSection: {
    eyebrow: "Choose your platform",
    titleLine1: "Use your favorite",
    titleAccent: "trading platform.",
    sub: "Trade on the tools you already know. Every plan connects to industry-standard platforms, no lock-in and no learning curve.",
  },
};

const DE: NewPricingCopy = {
  heroEyebrow: "Bau deinen Plan",
  heroTitleLine1: "Finanziere deinen Edge &",
  heroTitleAccent: "skaliere dein Trading.",
  heroSub: "Wähle dein Produkt, bestehe einmal, werde ausgezahlt. Wähl den Account, der zu deinem Trading passt.",

  markets: { futures: "Futures", cfdCrypto: "CFD + Crypto" },
  integratedWith: {
    cfdCrypto: "Crypto & CFD integriert mit",
    futures: "Futures integriert mit",
  },
  badgeNew: "Neu",
  badgeMostPopular: "Am beliebtesten",

  products: {
    core: {
      name: "Core",
      tag: "One-Day-Pass",
      info: "Unsere klassische, günstigste Evaluation. Erreiche das Gewinnziel ohne Drawdown-Verletzung — bestehen ist schon an einem Tag möglich — und trade dann einen Funded-Account.",
    },
    rapid: {
      name: "Rapid",
      tag: "Schnelle Payouts",
      info: "Auf Tempo gebaut. Niedrigere Gewinnziele und, sobald funded, Auszahlungen bis zu täglich. Kostenlose Aktivierung und kostenlose Neuversuche.",
    },
    instant: {
      name: "Instant Funding",
      tag: "Challenge überspringen",
      info: "Keine Evaluation. Einmal zahlen und ab Tag 1 einen Funded-Account traden — du behältst 90 % deiner Gewinne.",
    },
  },

  stage: { challenge: "Challenge", funded: "Funded Regeln", instantOnly: "Ab Tag 1 funded" },
  keyFeatures: "Key Features",
  chosenPath: "Gewählter Pfad",
  variantLabels: {
    mpl: "Max Position Loss",
    dll: "Daily Loss Limit",
    classic: "Classic",
    daily: "Daily",
  },

  kpiLabels: {
    profitTarget: "Gewinnziel",
    profitSplit: "Gewinnbeteiligung",
    payoutEvery: "Auszahlung alle",
    maxDrawdown: "Maximaler Drawdown",
    drawdownMode: "Drawdown-Modus",
    maxPositionLoss: "Max Position Loss",
    dailyLossLimit: "Daily Loss Limit",
    consistency: "Konsistenz",
    duration: "Laufzeit",
    activationFee: "Aktivierungsgebühr",
    resetFee: "Reset-Gebühr",
    extensionFee: "Verlängerungsgebühr",
    maxAccounts: "Max. Accounts",
  },

  kpiTooltips: {
    profitTarget: "Der Gewinn, den du erreichen musst, um die Evaluation zu bestehen.",
    profitSplit: "Dein Anteil am Gewinn. 90 / 10 = du behältst 90 %.",
    payoutEvery: "Wie oft du eine Auszahlung beantragen kannst.",
    maxDrawdown:
      "Der maximale Betrag, um den dein Kontostand vom Startkapital fallen darf, bevor der Account verletzt wird (Breach).",
    drawdownMode:
      "EOD Trailing: Die Verlust-Grenze zieht mit deinem höchsten Tagesend-Kontostand mit — sie wandert nach oben, wenn du Gewinn machst, nie nach unten.",
    maxPositionLoss:
      "Maximal erlaubter Verlust einer einzelnen offenen Position. 2 Soft Breaches werden verziehen, der 3. ist ein Hard Breach.",
    dailyLossLimit:
      "Maximal erlaubter Verlust innerhalb eines Handelstages. 1 Soft Breach wird verziehen, der 2. ist ein Hard Breach.",
    consistency: "Kein einzelner Handelstag darf diesen Anteil deines Gesamtgewinns überschreiten.",
    duration: "Zeitlimit, um die Challenge abzuschließen.",
    activationFee: "Einmalige Gebühr, um den Funded-Account nach dem Bestehen zu aktivieren.",
    resetFee: "Kosten, um den Account nach einem Verstoß neu zu starten.",
    extensionFee: "Gebühr, um die Laufzeit der Challenge zu verlängern.",
    maxAccounts: "Wie viele Funded-Accounts du parallel betreiben kannst.",
  },

  values: {
    none: "Keine",
    free: "Kostenlos",
    dash: "—",
    drawdownMode: "EOD Trailing",
    days: (n) => `${n} Tage`,
    payoutDays: (n) => (n === 1 ? "1 Tag" : `${n} Tage`),
  },

  price: { label: "Preis", account: "Account", oneTimeFee: "einmalige Gebühr" },
  sizeUnavailableReason: "Auf dieser Plattform nicht verfügbar",

  footnotes: {
    mpl: "* Max Position Loss: 2 Soft Breaches werden verziehen, der 3. ist ein Hard Breach.",
    dll: "* Daily Loss Limit: 1 Soft Breach wird verziehen, der 2. ist ein Hard Breach.",
  },

  cta: {
    getPlan: "Plan holen",
    startChallenge: "Challenge starten",
    getFunded: "Jetzt Funded werden",
    viewVariant: (label) => `${label} ansehen`,
  },

  unsure: {
    title: "Noch unsicher, welches Produkt zu dir passt?",
    sub: "Stelle Pläne nebeneinander und sieh sofort, worin sie sich unterscheiden.",
    helpMeChoose: "Hilf mir wählen",
    compare: "Vergleichen",
  },

  compare: {
    title: "Pläne vergleichen",
    sub: "Wähle bis zu 4 Pläne. Hervorgehobene Zeilen zeigen die Unterschiede.",
    legendMpl: "*MPL = Max Position Loss",
    legendDll: "*DLL = Daily Loss Limit",
    oneTimeFee: "Einmalige Gebühr",
    challenge: "Challenge",
    emptySlot: "Wähle oben einen Plan",
    mobileHint: "Wischen für mehr Pläne",
  },

  platformsSection: {
    eyebrow: "Wähle deine Plattform",
    titleLine1: "Nutze deine",
    titleAccent: "Lieblings-Trading-Plattform.",
    sub: "Trade mit den Tools, die du schon kennst. Jeder Plan ist mit branchenüblichen Plattformen verbunden — kein Lock-in, keine Einarbeitung.",
  },
};

const COPY: Record<string, NewPricingCopy> = { en: EN, de: DE };

export function resolveNewPricingCopy(language?: string): NewPricingCopy {
  if (!language) return EN;
  return COPY[language] ?? EN;
}
